import { useState, useEffect } from 'react';
import {
  Plus, Save, X, Check, Trash2, Loader2, Star,
  ChevronRight, ChevronLeft, FileText, Eye, Globe,
  Calendar, MapPin, Search, Link as LinkIcon, Image as ImageIcon
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';

const CATEGORIES = [
  'Circuite Externe', 'Circuite Romania', 'City Break', 'Concerte'
];

const DEFAULT_OPERATORS = [
  'Paralela 45', 'Hello Holidays', 'Christian Tour', 'Eximtur', 'Eturia', 
  'Cocktail Holidays', 'Coral Travel', 'Karpaten', 'Join UP!', 
  'TUI TravelCenter', 'Dertour', 'Eurolines'
];

const EMPTY_PACKAGE = {
  title: '', slug: '', destination_slug: '', destination_name: '',
  departure_city: '', price: '', old_price: '', duration: '',
  departure_dates: [], short_description: '', operator: '',
  redirect_link: '', includes: [], includes_note: '', excludes: [],
  optional_excursions: [], optional_excursions_note: '',
  detailed_program: [], useful_info: '', category: '',
  image_url: '', is_early_booking: false, is_last_minute: false,
  transport_type: 'avion', is_active: true, country: '', continent: '',
  cities_visited: [], circuit_type: '', available_months: [],
  highlights: [], difficulty_level: 'moderat', group_size: '',
  guide_language: 'romana', keywords: []
};

const generateSlug = (text) => {
  if (!text) return '';
  return text.toLowerCase()
    .replace(/[ăâ]/g, 'a').replace(/[î]/g, 'i')
    .replace(/[ș]/g, 's').replace(/[ț]/g, 't')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const PackageForm = ({ initialData, onSave, onCancel, loading, continents = [], countries = [], operators = [], onAddOperator }) => {
  const [formData, setFormData] = useState(initialData || EMPTY_PACKAGE);
  const [errors, setErrors] = useState({});
  const [activeSection, setActiveSection] = useState('basic'); // For collapsible sections
  const [showPreview, setShowPreview] = useState(false);
  
  // Update form data when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      const normalized = {...initialData};
      // Normalize transport_type to lowercase
      if (normalized.transport_type) normalized.transport_type = normalized.transport_type.toLowerCase();
      setFormData(normalized);
    } else {
      setFormData(EMPTY_PACKAGE);
    }
  }, [initialData]);
  
  // List input states
  const [newInclude, setNewInclude] = useState('');
  const [newExclude, setNewExclude] = useState('');
  const [newDepartureDate, setNewDepartureDate] = useState('');
  const [newProgramDay, setNewProgramDay] = useState({ day: '', title: '', description: '', accommodation: '', meals: '' });
  const [newExcursion, setNewExcursion] = useState({ name: '', price: '' });
  // NEW: Circuit-specific input states
  const [newCity, setNewCity] = useState('');
  const [newHighlight, setNewHighlight] = useState('');
  const [newKeyword, setNewKeyword] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const addToList = (field, value, setter) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), value.trim()]
      }));
      setter('');
    }
  };

  const removeFromList = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const addExcursion = () => {
    if (newExcursion.name.trim() && newExcursion.price.trim()) {
      setFormData(prev => ({
        ...prev,
        optional_excursions: [...(prev.optional_excursions || []), { ...newExcursion }]
      }));
      setNewExcursion({ name: '', price: '' });
    }
  };

  const addProgramDay = () => {
    if (newProgramDay.day.trim() && newProgramDay.title.trim()) {
      setFormData(prev => ({
        ...prev,
        detailed_program: [...(prev.detailed_program || []), { ...newProgramDay }]
      }));
      setNewProgramDay({ day: '', title: '', description: '', accommodation: '', meals: '' });
    }
  };

  const removeProgramDay = (index) => {
    setFormData(prev => ({
      ...prev,
      detailed_program: prev.detailed_program.filter((_, i) => i !== index)
    }));
  };

  const removeExcursion = (index) => {
    setFormData(prev => ({
      ...prev,
      optional_excursions: prev.optional_excursions.filter((_, i) => i !== index)
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Titlul este obligatoriu';
    if (!formData.price.trim()) newErrors.price = 'Prețul este obligatoriu';
    if (!formData.duration.trim()) newErrors.duration = 'Durata este obligatorie';
    if (!formData.short_description.trim()) newErrors.short_description = 'Descrierea este obligatorie';
    if (!formData.operator) newErrors.operator = 'Operatorul este obligatoriu';
    if (!formData.redirect_link.trim()) newErrors.redirect_link = 'Link-ul este obligatoriu';
    if (!formData.category) newErrors.category = 'Categoria este obligatorie';
    if (!formData.departure_city?.trim()) newErrors.departure_city = 'Orașul de plecare este obligatoriu';
    
    // Validate URL
    if (formData.redirect_link && !formData.redirect_link.match(/^https?:\/\//)) {
      newErrors.redirect_link = 'URL invalid (trebuie să înceapă cu http:// sau https://)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Progress indicator */}
      <div className="flex items-center gap-2 mb-4 pb-4 border-b border-navy/10">
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${activeSection === 'basic' ? 'bg-gold text-navy' : 'bg-navy/10 text-navy/60'}`}>
          <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-[10px]">1</span>
          Informații de bază
        </div>
        <ChevronRight className="w-4 h-4 text-navy/30" />
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${activeSection === 'details' ? 'bg-gold text-navy' : 'bg-navy/10 text-navy/60'}`}>
          <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-[10px]">2</span>
          Detalii program
        </div>
        <ChevronRight className="w-4 h-4 text-navy/30" />
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${activeSection === 'preview' ? 'bg-gold text-navy' : 'bg-navy/10 text-navy/60'}`}>
          <span className="w-5 h-5 rounded-full bg-current/20 flex items-center justify-center text-[10px]">3</span>
          Preview & Salvare
        </div>
      </div>

      {/* Section 1: Basic Info */}
      {activeSection === 'basic' && (
        <div className="space-y-4 animate-in fade-in duration-300">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-gold" />
            <h4 className="font-heading font-semibold text-navy">Informații de bază</h4>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Title */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-navy mb-1">
                Titlu <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => {
                  const newTitle = e.target.value;
                  handleChange('title', newTitle);
                  if (!formData.slug || formData.slug === generateSlug(formData.title)) {
                    handleChange('slug', generateSlug(newTitle));
                  }
                }}
                placeholder="ex: Circuit Italia Nord – Veneția, Verona, Milano"
                className={`w-full px-4 py-2.5 rounded-xl border bg-white text-navy ${errors.title ? 'border-red-500' : 'border-navy/20'} focus:border-gold focus:ring-1 focus:ring-gold/50`}
                data-testid="package-title-input"
              />
              {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
              <p className="text-xs text-navy/50 mt-1">Titlul va apărea pe card și în rezultatele căutării</p>
            </div>

            {/* Price & Old Price Row */}
            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Preț <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  placeholder="999 €"
                  className={`w-full px-4 py-2.5 rounded-xl border bg-white text-navy ${errors.price ? 'border-red-500' : 'border-navy/20'} focus:border-gold focus:ring-1 focus:ring-gold/50`}
                  data-testid="package-price-input"
                />
              </div>
              {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
              <p className="text-xs text-navy/50 mt-1">Format: 999 € sau 999 €/pers.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Preț vechi <span className="text-navy/50">(opțional - pentru reduceri)</span>
              </label>
              <input
                type="text"
                value={formData.old_price || ''}
                onChange={(e) => handleChange('old_price', e.target.value)}
                placeholder="1199 €"
                className="w-full px-4 py-2.5 rounded-xl border bg-white text-navy border-navy/20 focus:border-gold focus:ring-1 focus:ring-gold/50"
              />
              <p className="text-xs text-navy/50 mt-1">Va apărea tăiat lângă prețul nou</p>
            </div>

            {/* Duration & Operator Row */}
            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Durată <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.duration}
                onChange={(e) => handleChange('duration', e.target.value)}
                placeholder="8 zile / 7 nopți"
                className={`w-full px-4 py-2.5 rounded-xl border bg-white text-navy ${errors.duration ? 'border-red-500' : 'border-navy/20'} focus:border-gold focus:ring-1 focus:ring-gold/50`}
                data-testid="package-duration-input"
              />
              {errors.duration && <p className="text-red-500 text-xs mt-1">{errors.duration}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Operator turistic <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <Select value={formData.operator} onValueChange={(v) => handleChange('operator', v)}>
                  <SelectTrigger className={`flex-1 bg-white text-navy ${errors.operator ? 'border-red-500' : 'border-navy/20'}`} data-testid="package-operator-select">
                    <SelectValue placeholder="Selectează operator" />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-60 overflow-y-auto">
                    {(operators.length > 0 
                      ? operators.filter(op => op.is_active).map(op => op.name) 
                      : DEFAULT_OPERATORS
                    ).map(name => (
                      <SelectItem key={name} value={name} className="text-navy cursor-pointer hover:bg-navy/5">{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={onAddOperator}
                  className="border-navy/20 hover:bg-navy/5"
                  title="Adaugă operator nou"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {errors.operator && <p className="text-red-500 text-xs mt-1">{errors.operator}</p>}
            </div>

            {/* Category & Transport Row */}
            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Categorie <span className="text-red-500">*</span>
              </label>
              <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                <SelectTrigger className={`w-full bg-white text-navy ${errors.category ? 'border-red-500' : 'border-navy/20'}`} data-testid="package-category-select">
                  <SelectValue placeholder="Selectează categorie" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
              <p className="text-xs text-navy/50 mt-1">Determină în ce secțiune va apărea pachetul</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Tip transport
              </label>
              <Select value={formData.transport_type} onValueChange={(v) => handleChange('transport_type', v)}>
                <SelectTrigger className="w-full bg-white text-navy border-navy/20">
                  <SelectValue placeholder="Selectează transport" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="avion">✈️ Avion</SelectItem>
                  <SelectItem value="autocar">🚌 Autocar</SelectItem>
                  <SelectItem value="individual">🚗 Individual / Self-drive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Continent & Country Row */}
            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Continent
              </label>
              <Select 
                value={formData.continent || ''} 
                onValueChange={(v) => {
                  handleChange('continent', v);
                  handleChange('country', '');
                }}
              >
                <SelectTrigger className="w-full bg-white text-navy border-navy/20">
                  <SelectValue placeholder="Selectează continent" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {continents.filter(c => c.is_active !== false).map(cont => (
                    <SelectItem key={cont.id} value={cont.name}>{cont.name}</SelectItem>
                  ))}
                  <SelectItem value="Multiple">🌍 Multiple continente</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Țară
              </label>
              <Select 
                value={formData.country || ''} 
                onValueChange={(v) => handleChange('country', v)}
                disabled={!formData.continent || formData.continent === 'Multiple'}
              >
                <SelectTrigger className={`w-full bg-white text-navy border-navy/20 ${(!formData.continent || formData.continent === 'Multiple') ? 'opacity-50' : ''}`}>
                  <SelectValue placeholder={formData.continent === 'Multiple' ? 'Multiple țări' : (formData.continent ? 'Selectează țara' : 'Selectează mai întâi continentul')} />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  {formData.continent && formData.continent !== 'Multiple' && 
                    countries
                      .filter(c => c.continent_name === formData.continent && c.is_active !== false)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(country => (
                        <SelectItem key={country.id} value={country.name}>{country.name}</SelectItem>
                      ))
                  }
                </SelectContent>
              </Select>
              {formData.continent && formData.continent !== 'Multiple' && countries.filter(c => c.continent_name === formData.continent).length === 0 && (
                <p className="text-amber-600 text-xs mt-1">⚠️ Nu există țări pentru acest continent. Adăugați din tab-ul "Continente & Țări".</p>
              )}
            </div>

            {/* Departure City - Plecare din */}
            <div>
              <label className="block text-sm font-medium text-navy mb-1">
                Plecare din <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
                <input
                  type="text"
                  value={formData.departure_city || ''}
                  onChange={(e) => handleChange('departure_city', e.target.value)}
                  placeholder="ex: București, Cluj-Napoca, Timișoara, Iași, Brașov"
                  className={`w-full pl-10 pr-4 py-2.5 rounded-xl border bg-white text-navy ${errors.departure_city ? 'border-red-500' : 'border-navy/20'} focus:border-gold focus:ring-1 focus:ring-gold/50`}
                  data-testid="package-departure-city-input"
                />
              </div>
              {errors.departure_city && <p className="text-red-500 text-xs mt-1">{errors.departure_city}</p>}
              <p className="text-xs text-navy/50 mt-1">Orașul de plecare pentru acest pachet turistic</p>
            </div>

            {/* Redirect Link */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-navy mb-1">
                Link afiliat / redirecționare <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
                <input
                  type="url"
                  value={formData.redirect_link}
                  onChange={(e) => handleChange('redirect_link', e.target.value)}
                  placeholder="https://touroperator.ro/pachet"
                  className={`w-full pl-10 pr-4 py-2.5 rounded-xl border bg-white text-navy ${errors.redirect_link ? 'border-red-500' : 'border-navy/20'} focus:border-gold focus:ring-1 focus:ring-gold/50`}
                  data-testid="package-link-input"
                />
              </div>
              {errors.redirect_link && <p className="text-red-500 text-xs mt-1">{errors.redirect_link}</p>}
              <p className="text-xs text-navy/50 mt-1">Link-ul către pagina originală a pachetului de pe site-ul tour operatorului</p>
            </div>

            {/* Image URL with better preview */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-navy mb-1">
                URL Imagine principală
              </label>
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => handleChange('image_url', e.target.value)}
                    placeholder="https://example.com/image.jpg"
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border bg-white text-navy border-navy/20 focus:border-gold focus:ring-1 focus:ring-gold/50"
                  />
                </div>
                {formData.image_url && (
                  <div className="w-20 h-14 rounded-lg overflow-hidden border-2 border-navy/20 flex-shrink-0">
                    <img 
                      src={formData.image_url} 
                      alt="Preview" 
                      className="w-full h-full object-cover" 
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }} 
                    />
                    <div className="hidden w-full h-full bg-red-50 items-center justify-center">
                      <span className="text-red-500 text-xs">Eroare</span>
                    </div>
                  </div>
                )}
              </div>
              <p className="text-xs text-navy/50 mt-1">Recomandăm imagini de minim 800x600px</p>
            </div>

            {/* Short Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-navy mb-1">
                Descriere scurtă <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.short_description}
                onChange={(e) => handleChange('short_description', e.target.value)}
                placeholder="O descriere concisă care va apărea pe cardul pachetului..."
                rows={2}
                className={`w-full px-4 py-2.5 rounded-xl border bg-white text-navy ${errors.short_description ? 'border-red-500' : 'border-navy/20'} focus:border-gold focus:ring-1 focus:ring-gold/50 resize-none`}
                data-testid="package-description-input"
              />
              {errors.short_description && <p className="text-red-500 text-xs mt-1">{errors.short_description}</p>}
              <p className="text-xs text-navy/50 mt-1">{formData.short_description?.length || 0}/200 caractere recomandate</p>
            </div>

            {/* Status toggles */}
            <div className="md:col-span-2 flex flex-wrap items-center gap-4 p-4 bg-navy/5 rounded-xl">
              <span className="text-sm font-medium text-navy mr-2">Status:</span>
              
              <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-2 rounded-lg border border-navy/10 hover:border-gold transition-colors">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.is_active !== false}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                </div>
                <span className={`text-sm ${formData.is_active !== false ? 'text-emerald-600 font-medium' : 'text-gray-500'}`}>
                  {formData.is_active !== false ? 'Activ' : 'Inactiv'}
                </span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-2 rounded-lg border border-navy/10 hover:border-gold transition-colors">
                <input
                  type="checkbox"
                  checked={formData.is_early_booking}
                  onChange={(e) => handleChange('is_early_booking', e.target.checked)}
                  className="w-4 h-4 rounded border-navy/30 text-gold focus:ring-gold"
                />
                <span className={`text-sm ${formData.is_early_booking ? 'text-gold font-medium' : 'text-gray-500'}`}>Early Booking</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-2 rounded-lg border border-navy/10 hover:border-gold transition-colors">
                <input
                  type="checkbox"
                  checked={formData.is_last_minute}
                  onChange={(e) => handleChange('is_last_minute', e.target.checked)}
                  className="w-4 h-4 rounded border-navy/30 text-gold focus:ring-gold"
                />
                <span className={`text-sm ${formData.is_last_minute ? 'text-orange-500 font-medium' : 'text-gray-500'}`}>Last Minute</span>
              </label>
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="flex justify-between pt-4 border-t border-navy/10">
            <Button type="button" variant="outline" onClick={onCancel} className="border-navy/20 text-navy hover:bg-navy/5">
              <X className="w-4 h-4 mr-2" />
              Anulează
            </Button>
            <Button 
              type="button" 
              onClick={() => {
                if (validate()) {
                  setActiveSection('details');
                }
              }}
              className="bg-gold hover:bg-gold/90 text-navy"
            >
              Continuă
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Section 2: Details */}
      {activeSection === 'details' && (
        <div className="space-y-4 animate-in fade-in duration-300">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-gold" />
            <h4 className="font-heading font-semibold text-navy">Detalii suplimentare</h4>
            <span className="text-xs text-navy/50">(opționale)</span>
          </div>

          {/* SEO Fields */}
          <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-200/50">
            <h5 className="text-sm font-medium text-navy mb-3 flex items-center gap-2">
              <Globe className="w-4 h-4 text-blue-500" />
              SEO & URL-uri
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-navy/70 mb-1">Slug URL</label>
                <input
                  type="text"
                  value={formData.slug || ''}
                  onChange={(e) => handleChange('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-'))}
                  placeholder="circuit-italia-nord"
                  className="w-full px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm font-mono focus:border-gold"
                />
                <p className="text-xs text-navy/50 mt-1">URL: /oferte/{formData.destination_slug || 'destinatie'}/{formData.slug || 'slug'}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-navy/70 mb-1">Destinație (pentru SEO)</label>
                <input
                  type="text"
                  value={formData.destination_name || ''}
                  onChange={(e) => {
                    const newDest = e.target.value;
                    handleChange('destination_name', newDest);
                    handleChange('destination_slug', generateSlug(newDest));
                  }}
                  placeholder="Italia, Roma"
                  className="w-full px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
                />
              </div>
            </div>
          </div>

          {/* Circuit-Specific Fields - Only show for Circuite categories */}
          {(formData.category === 'Circuite Externe' || formData.category === 'Circuite Romania') && (
            <div className="p-4 bg-amber-50/50 rounded-xl border border-amber-200/50">
              <h5 className="text-sm font-medium text-navy mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-amber-600" />
                Detalii Circuit (pentru recomandări AI mai bune)
              </h5>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Circuit Type */}
                <div>
                  <label className="block text-xs font-medium text-navy/70 mb-1">Tip Circuit</label>
                  <select
                    value={formData.circuit_type || ''}
                    onChange={(e) => handleChange('circuit_type', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
                  >
                    <option value="">Selectează tipul</option>
                    <option value="cultural">Cultural - Vizite muzee, monumente</option>
                    <option value="aventura">Aventură - Drumeții, activități</option>
                    <option value="gastronomic">Gastronomic - Degustări, bucătărie locală</option>
                    <option value="religios">Religios - Pelerinaje, mănăstiri</option>
                    <option value="romantic">Romantic - Cupluri, lună de miere</option>
                    <option value="familie">Familie - Potrivit pentru copii</option>
                    <option value="mixt">Mixt - Combinat</option>
                  </select>
                </div>

                {/* Difficulty Level */}
                <div>
                  <label className="block text-xs font-medium text-navy/70 mb-1">Nivel Dificultate</label>
                  <select
                    value={formData.difficulty_level || 'moderat'}
                    onChange={(e) => handleChange('difficulty_level', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
                  >
                    <option value="usor">Ușor - Fără efort fizic</option>
                    <option value="moderat">Moderat - Mers pe jos moderat</option>
                    <option value="intens">Intens - Drumeții lungi</option>
                  </select>
                </div>

                {/* Group Size */}
                <div>
                  <label className="block text-xs font-medium text-navy/70 mb-1">Mărime Grup</label>
                  <input
                    type="text"
                    value={formData.group_size || ''}
                    onChange={(e) => handleChange('group_size', e.target.value)}
                    placeholder="ex: max 25 persoane"
                    className="w-full px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
                  />
                </div>

                {/* Guide Language */}
                <div>
                  <label className="block text-xs font-medium text-navy/70 mb-1">Limba Ghidului</label>
                  <select
                    value={formData.guide_language || 'romana'}
                    onChange={(e) => handleChange('guide_language', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
                  >
                    <option value="romana">Română</option>
                    <option value="engleza">Engleză</option>
                    <option value="romana_engleza">Română + Engleză</option>
                    <option value="locala">Ghid local (cu traducere)</option>
                  </select>
                </div>
              </div>

              {/* Cities Visited */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-navy/70 mb-1">
                  Orașe Vizitate <span className="text-navy/40">(ajută AI să găsească "vreau să văd Roma")</span>
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newCity}
                    onChange={(e) => setNewCity(e.target.value)}
                    placeholder="ex: Roma, Florența, Veneția"
                    className="flex-1 px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addToList('cities_visited', newCity, setNewCity);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={() => addToList('cities_visited', newCity, setNewCity)}
                    variant="outline"
                    size="sm"
                    className="border-navy/20"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.cities_visited?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.cities_visited.map((city, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded-lg text-sm">
                        <MapPin className="w-3 h-3" />
                        {city}
                        <button type="button" onClick={() => removeFromList('cities_visited', i)} className="hover:text-red-500 ml-1">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Highlights */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-navy/70 mb-1">
                  Atracții Principale <span className="text-navy/40">(Colosseum, Turnul Eiffel, etc.)</span>
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newHighlight}
                    onChange={(e) => setNewHighlight(e.target.value)}
                    placeholder="ex: Colosseum, Vatican, Turnul din Pisa"
                    className="flex-1 px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addToList('highlights', newHighlight, setNewHighlight);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={() => addToList('highlights', newHighlight, setNewHighlight)}
                    variant="outline"
                    size="sm"
                    className="border-navy/20"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.highlights?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.highlights.map((item, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-lg text-sm">
                        <Star className="w-3 h-3" />
                        {item}
                        <button type="button" onClick={() => removeFromList('highlights', i)} className="hover:text-red-500 ml-1">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Available Months */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-navy/70 mb-1">
                  Luni Disponibile <span className="text-navy/40">(când sunt plecări)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {['ianuarie', 'februarie', 'martie', 'aprilie', 'mai', 'iunie', 'iulie', 'august', 'septembrie', 'octombrie', 'noiembrie', 'decembrie'].map((month) => (
                    <button
                      key={month}
                      type="button"
                      onClick={() => {
                        const months = formData.available_months || [];
                        if (months.includes(month)) {
                          handleChange('available_months', months.filter(m => m !== month));
                        } else {
                          handleChange('available_months', [...months, month]);
                        }
                      }}
                      className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                        formData.available_months?.includes(month)
                          ? 'bg-gold text-navy'
                          : 'bg-navy/5 text-navy/60 hover:bg-navy/10'
                      }`}
                    >
                      {month.charAt(0).toUpperCase() + month.slice(1, 3)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Keywords for AI */}
              <div>
                <label className="block text-xs font-medium text-navy/70 mb-1">
                  Keywords AI <span className="text-navy/40">(cuvinte cheie pentru căutare)</span>
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    placeholder="ex: romantic, all-inclusive, plajă, munte"
                    className="flex-1 px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addToList('keywords', newKeyword, setNewKeyword);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={() => addToList('keywords', newKeyword, setNewKeyword)}
                    variant="outline"
                    size="sm"
                    className="border-navy/20"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.keywords?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.keywords.map((kw, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-lg text-sm">
                        <Search className="w-3 h-3" />
                        {kw}
                        <button type="button" onClick={() => removeFromList('keywords', i)} className="hover:text-red-500 ml-1">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-1">
                  <span className="text-xs text-navy/40">Sugestii:</span>
                  {['romantic', 'familie', 'aventură', 'cultural', 'gastronomic', 'relax', 'activ', 'all-inclusive', 'low-cost', 'lux'].map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => {
                        if (!formData.keywords?.includes(suggestion)) {
                          handleChange('keywords', [...(formData.keywords || []), suggestion]);
                        }
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      +{suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Departure Dates */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Date de plecare
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newDepartureDate}
                onChange={(e) => setNewDepartureDate(e.target.value)}
                placeholder="ex: 15.04.2026 sau Aprilie 2026"
                className="flex-1 px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addToList('departure_dates', newDepartureDate, setNewDepartureDate);
                  }
                }}
              />
              <Button
                type="button"
                onClick={() => addToList('departure_dates', newDepartureDate, setNewDepartureDate)}
                variant="outline"
                size="sm"
                className="border-navy/20"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {formData.departure_dates?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.departure_dates.map((date, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-gold/10 text-navy rounded-lg text-sm">
                    <Calendar className="w-3 h-3" />
                    {date}
                    <button type="button" onClick={() => removeFromList('departure_dates', i)} className="hover:text-red-500 ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Includes */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Ce include pachetul
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newInclude}
                onChange={(e) => setNewInclude(e.target.value)}
                placeholder="ex: Transport avion, Cazare 4*, Mic dejun"
                className="flex-1 px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addToList('includes', newInclude, setNewInclude);
                  }
                }}
              />
              <Button
                type="button"
                onClick={() => addToList('includes', newInclude, setNewInclude)}
                variant="outline"
                size="sm"
                className="border-navy/20"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {formData.includes?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.includes.map((item, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-sm">
                    <Check className="w-3 h-3" />
                    {item}
                    <button type="button" onClick={() => removeFromList('includes', i)} className="hover:text-red-500 ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Excludes */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Ce NU include pachetul
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newExclude}
                onChange={(e) => setNewExclude(e.target.value)}
                placeholder="ex: Excursii opționale, Asigurare"
                className="flex-1 px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addToList('excludes', newExclude, setNewExclude);
                  }
                }}
              />
              <Button
                type="button"
                onClick={() => addToList('excludes', newExclude, setNewExclude)}
                variant="outline"
                size="sm"
                className="border-navy/20"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {formData.excludes?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.excludes.map((item, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 rounded-lg text-sm">
                    <X className="w-3 h-3" />
                    {item}
                    <button type="button" onClick={() => removeFromList('excludes', i)} className="hover:text-red-500 ml-1">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Detailed Program */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Program detaliat (pe zile)
            </label>
            <div className="space-y-3 mb-3 p-4 bg-navy/5 rounded-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  type="text"
                  value={newProgramDay.day}
                  onChange={(e) => setNewProgramDay(prev => ({ ...prev, day: e.target.value }))}
                  placeholder="Ziua (ex: Ziua 1)"
                  className="px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
                />
                <input
                  type="text"
                  value={newProgramDay.title}
                  onChange={(e) => setNewProgramDay(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Titlu (ex: București – Viena)"
                  className="px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
                />
              </div>
              <textarea
                value={newProgramDay.description}
                onChange={(e) => setNewProgramDay(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descriere activități pentru această zi..."
                rows={2}
                className="w-full px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold resize-none"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input
                  type="text"
                  value={newProgramDay.accommodation}
                  onChange={(e) => setNewProgramDay(prev => ({ ...prev, accommodation: e.target.value }))}
                  placeholder="Cazare (ex: Hotel 4*, Viena)"
                  className="px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
                />
                <input
                  type="text"
                  value={newProgramDay.meals}
                  onChange={(e) => setNewProgramDay(prev => ({ ...prev, meals: e.target.value }))}
                  placeholder="Mese (ex: Mic dejun inclus)"
                  className="px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
                />
              </div>
              <Button 
                type="button" 
                onClick={addProgramDay} 
                variant="outline"
                className="w-full border-navy/20 text-navy hover:bg-navy/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adaugă ziua în program
              </Button>
            </div>
            
            {/* List of added program days */}
            {formData.detailed_program?.length > 0 && (
              <div className="space-y-2 mt-3">
                <p className="text-xs text-navy/60 font-medium">Zile adăugate ({formData.detailed_program.length}):</p>
                {formData.detailed_program.map((day, i) => (
                  <div key={i} className="p-3 bg-white border border-navy/10 rounded-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="font-medium text-navy text-sm">{day.day}: {day.title}</p>
                        {day.description && <p className="text-navy/60 text-xs mt-1 line-clamp-2">{day.description}</p>}
                        {(day.accommodation || day.meals) && (
                          <div className="flex gap-4 mt-1 text-xs text-navy/50">
                            {day.accommodation && <span>🏨 {day.accommodation}</span>}
                            {day.meals && <span>🍽️ {day.meals}</span>}
                          </div>
                        )}
                      </div>
                      <button 
                        type="button" 
                        onClick={() => removeProgramDay(i)} 
                        className="text-red-500 hover:text-red-700 flex-shrink-0 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Optional Excursions */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Excursii opționale
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newExcursion.name}
                onChange={(e) => setNewExcursion(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nume excursie"
                className="flex-1 px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
              />
              <input
                type="text"
                value={newExcursion.price}
                onChange={(e) => setNewExcursion(prev => ({ ...prev, price: e.target.value }))}
                placeholder="Preț (ex: 50 €)"
                className="w-28 px-3 py-2 rounded-lg border bg-white text-navy border-navy/20 text-sm focus:border-gold"
              />
              <Button
                type="button"
                onClick={addExcursion}
                variant="outline"
                size="sm"
                className="border-navy/20"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {formData.optional_excursions?.length > 0 && (
              <div className="space-y-1">
                {formData.optional_excursions.map((exc, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 bg-purple-50 rounded-lg text-sm">
                    <span className="text-purple-700">{exc.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gold font-medium">{exc.price}</span>
                      <button type="button" onClick={() => removeExcursion(i)} className="text-red-500 hover:text-red-700">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Useful Info */}
          <div>
            <label className="block text-sm font-medium text-navy mb-2">
              Informații utile
            </label>
            <textarea
              value={formData.useful_info || ''}
              onChange={(e) => handleChange('useful_info', e.target.value)}
              placeholder="Informații suplimentare despre pachet, documente necesare, etc..."
              rows={3}
              className="w-full px-4 py-2.5 rounded-xl border bg-white text-navy border-navy/20 focus:border-gold focus:ring-1 focus:ring-gold/50 resize-none"
            />
          </div>

          {/* Navigation buttons */}
          <div className="flex justify-between pt-4 border-t border-navy/10">
            <Button type="button" variant="outline" onClick={() => setActiveSection('basic')} className="border-navy/20 text-navy hover:bg-navy/5">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Înapoi
            </Button>
            <Button 
              type="button" 
              onClick={() => setActiveSection('preview')}
              className="bg-gold hover:bg-gold/90 text-navy"
            >
              Preview
              <Eye className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Section 3: Preview & Save */}
      {activeSection === 'preview' && (
        <div className="space-y-4 animate-in fade-in duration-300">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5 text-gold" />
            <h4 className="font-heading font-semibold text-navy">Preview & Salvare</h4>
          </div>

          {/* Live Preview Card */}
          <div className="p-6 bg-navy rounded-xl">
            <p className="text-white/60 text-sm mb-4">Așa va arăta cardul pe site:</p>
            <div className="max-w-xs mx-auto">
              <div className="glass-card rounded-xl overflow-hidden">
                <div className="relative h-40">
                  {formData.image_url ? (
                    <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-navy/30 flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-white/30" />
                    </div>
                  )}
                  <span className="absolute top-2 right-2 bg-navy/80 text-white text-xs px-2 py-1 rounded-full">
                    {formData.operator || 'Operator'}
                  </span>
                  {formData.transport_type && (
                    <span className="absolute bottom-2 left-2 bg-white/90 text-navy text-xs px-2 py-1 rounded-full flex items-center gap-1">
                      {formData.transport_type === 'avion' ? '✈️' : formData.transport_type === 'autocar' ? '🚌' : '🚗'}
                      {formData.transport_type === 'avion' ? 'Avion' : formData.transport_type === 'autocar' ? 'Autocar' : 'Individual'}
                    </span>
                  )}
                </div>
                <div className="p-3">
                  <div className="text-white/60 text-xs mb-1">{formData.duration || '8 zile'}</div>
                  <h3 className="text-white font-semibold text-sm mb-2 line-clamp-2">{formData.title || 'Titlu pachet'}</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-white/50 text-xs">de la</span>
                    {formData.old_price && <span className="text-white/40 line-through text-xs">{formData.old_price}</span>}
                    <span className="text-gold font-bold text-lg">{formData.price || '999 €'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="p-4 bg-navy/5 rounded-xl">
            <h5 className="font-medium text-navy mb-3">Rezumat</h5>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-navy/60">Categorie:</span> <span className="font-medium">{formData.category || '-'}</span></div>
              <div><span className="text-navy/60">Continent:</span> <span className="font-medium">{formData.continent || '-'}</span></div>
              <div><span className="text-navy/60">Țară:</span> <span className="font-medium">{formData.country || '-'}</span></div>
              <div><span className="text-navy/60">Transport:</span> <span className="font-medium">{formData.transport_type || '-'}</span></div>
              <div><span className="text-navy/60">Date plecare:</span> <span className="font-medium">{formData.departure_dates?.length || 0} adăugate</span></div>
              <div><span className="text-navy/60">Include:</span> <span className="font-medium">{formData.includes?.length || 0} elemente</span></div>
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="flex justify-between pt-4 border-t border-navy/10">
            <Button type="button" variant="outline" onClick={() => setActiveSection('details')} className="border-navy/20 text-navy hover:bg-navy/5">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Înapoi
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onCancel} className="border-navy/20 text-navy hover:bg-navy/5">
                Anulează
              </Button>
              <Button 
                type="submit"
                disabled={loading}
                className="bg-emerald-500 hover:bg-emerald-600 text-white min-w-32"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {initialData?.id ? 'Salvează modificările' : 'Creează pachetul'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};



export { PackageForm, EMPTY_PACKAGE };
